import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LoginReportModal from './LoginReportModal';
import './Staff.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const SUPABASE_URL = 'https://yulypcgdgxzqtdkysywg.supabase.co';
const SUPABASE_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inl1bHlwY2dkZ3h6cXRka3lzeXdnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzcwMzM4NDcsImV4cCI6MjA5MjYwOTg0N30.jeMCN3jQaIzdZor8I_ZdKLg5pBx6iiPdGQdaV26RrUw';

export default function Staff({ user }) {
  const [staff, setStaff] = useState([]);
  const [selected, setSelected] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [inductionDetail, setInductionDetail] = useState(null);
  const [inductionSections, setInductionSections] = useState([]);
  const [startingInduction, setStartingInduction] = useState(false);
  const [trainingMap, setTrainingMap] = useState({});
  const [inductionMap, setInductionMap] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  const [showLoginReport, setShowLoginReport] = useState(false);
  const [addingNew, setAddingNew] = useState(false);
  const [newForm, setNewForm] = useState({ first_name: '', last_name: '', email: '', job_title: '', role: 'staff', phone: '', date_of_birth: '', start_date: '', ni_number: '' });
  const [addingSaving, setAddingSaving] = useState(false);

  useEffect(() => { fetchStaff(); }, []);

  const fetchStaff = async () => {
    try {
      const res = await axios.get(`${API}/users/all`);
      setStaff(res.data);
      const trainingData = {};
      const inductionData = {};
      await Promise.all(res.data.map(async (u) => {
        try {
          const [t, ind] = await Promise.all([
            axios.get(`${API}/training/user/${u.id}`),
            axios.get(`${API}/inductions/user/${u.id}`),
          ]);
          trainingData[u.id] = t.data;
          inductionData[u.id] = ind.data;
        } catch {}
      }));
      setTrainingMap(trainingData);
      setInductionMap(inductionData);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchProfile = async (staffUser) => {
    setSelected(staffUser);
    setEditForm(staffUser);
    setProfileLoading(true);
    setProfile(null);
    setEditing(false);
    setInductionDetail(null);
    try {
      const [trainingRes, inductionRes] = await Promise.all([
        axios.get(`${API}/training/user/${staffUser.id}`),
        axios.get(`${API}/inductions/user/${staffUser.id}`),
      ]);
      setProfile({ training: trainingRes.data, inductions: inductionRes.data });
    } catch (err) { console.error(err); }
    finally { setProfileLoading(false); }
  };

  const fetchInductionDetail = async (ind) => {
    try {
      const res = await axios.get(`${API}/inductions/${ind.id}`);
      setInductionSections(res.data.sections || []);
      setInductionDetail(res.data);
    } catch (err) { alert('Error loading induction details.'); }
  };

  const handleArchiveTraining = async (trainingId) => {
    if (!window.confirm('Archive this training record? It will be removed from view but kept in the system.')) return;
    try {
      await axios.patch(`${API}/training/${trainingId}/archive`, { archived_by: user.id });
      setProfile(prev => ({
        ...prev,
        training: prev.training.filter(t => t.id !== trainingId)
      }));
    } catch (err) { alert('Error archiving training record.'); }
  };

  const [selectedTraining, setSelectedTraining] = useState(null);
  const [showTrainingForm, setShowTrainingForm] = useState(false);
  const [trainingForm, setTrainingForm] = useState({
    training_type: '', provider: '', issue_date: '', expiry_date: '',
    certificate_number: '', certificate_url: '', notes: '',
  });
  const [uploadingTraining, setUploadingTraining] = useState(false);
  const [analysingTraining, setAnalysingTraining] = useState(false);
  const [savingTraining, setSavingTraining] = useState(false);
  const trainingFileRef = React.useRef();

  const toBase64 = (f) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result.split(',')[1]);
    r.onerror = rej;
    r.readAsDataURL(f);
  });

  const uploadAndAnalyseTraining = async (file) => {
    if (!file) return;
    setUploadingTraining(true);
    try {
      const ext = file.name.split('.').pop();
      const safeName = `${selected.first_name}-${selected.last_name}`.toLowerCase().replace(/[^a-z0-9]/g, '-');
      const path = `training-records/${safeName}/${Date.now()}.${ext}`;
      const res = await fetch(`${SUPABASE_URL}/storage/v1/object/vera-documents/${path}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${SUPABASE_KEY}`,
          'Content-Type': file.type,
          'x-upsert': 'true',
        },
        body: file,
      });
      console.log('Storage upload status:', res.status, res.statusText);
      const resText = await res.text();
      console.log('Storage upload response:', resText);
      if (res.ok) {
        const url = `${SUPABASE_URL}/storage/v1/object/public/vera-documents/${path}`;
        setTrainingForm(prev => ({ ...prev, certificate_url: url }));
      }
    } catch (err) { console.error('Upload error:', err); }
    finally { setUploadingTraining(false); }

    // Analyse with Claude
    if (file.type === 'application/pdf' || file.type.startsWith('image/')) {
      setAnalysingTraining(true);
      try {
        const base64 = await toBase64(file);
        const analyseRes = await axios.post(`${API}/analyse/training-cert`, {
          base64, mediaType: file.type,
          staffName: `${selected.first_name} ${selected.last_name}`,
        });
        const data = analyseRes.data;
        setTrainingForm(prev => ({
          ...prev,
          training_type: data.training_type || prev.training_type,
          provider: data.provider || prev.provider,
          issue_date: data.issue_date || prev.issue_date,
          expiry_date: data.expiry_date || prev.expiry_date,
          certificate_number: data.certificate_number || prev.certificate_number,
          notes: data.notes || prev.notes,
        }));
      } catch (e) { console.log('Training cert analysis failed:', e.message); }
      finally { setAnalysingTraining(false); }
    }
  };

  const saveTrainingRecord = async () => {
    if (!trainingForm.training_type) { alert('Training type is required.'); return; }
    setSavingTraining(true);
    try {
      await axios.post(`${API}/training`, {
        ...trainingForm,
        user_id: selected.id,
        date_completed: trainingForm.issue_date || null,
        issue_date: undefined,
        expiry_date: trainingForm.expiry_date || null,
        created_by: user.id,
      });
      setShowTrainingForm(false);
      setTrainingForm({ training_type: '', provider: '', issue_date: '', expiry_date: '', certificate_number: '', certificate_url: '', notes: '' });
      fetchProfile(selected);
    } catch (err) { alert('Error saving: ' + err.message); }
    finally { setSavingTraining(false); }
  };

  const toggleSection = async (section) => {
    if (inductionDetail?.status === 'completed') return;
    try {
      await axios.patch(`${API}/inductions/${inductionDetail.id}/section/${section.id}`, {
        completed: !section.completed,
        completed_by: user.id,
      });
      setInductionSections(prev => prev.map(s =>
        s.id === section.id ? { ...s, completed: !s.completed, completed_at: !s.completed ? new Date().toISOString() : null } : s
      ));
    } catch (err) { alert('Error updating section: ' + err.message); }
  };

  const employeeSignOff = async () => {
    try {
      await axios.patch(`${API}/inductions/${inductionDetail.id}/employee-sign`, { signed_by: user.id });
      setInductionDetail(prev => ({ ...prev, employee_signed: true, employee_signed_at: new Date().toISOString() }));
    } catch (err) { alert('Error signing off.'); }
  };

  const completeInduction = async () => {
    try {
      await axios.patch(`${API}/inductions/${inductionDetail.id}/complete`, { signed_by: user.id });
      setInductionDetail(prev => ({ ...prev, company_rep_signed: true, status: 'completed' }));
      try { await axios.post(`${API}/inductions/${inductionDetail.id}/certificate`); } catch (e) { console.error('Cert error:', e); }
      const indRes = await axios.get(`${API}/inductions/user/${selected.id}`);
      setProfile(prev => ({ ...prev, inductions: indRes.data }));
      fetchStaff();
    } catch (err) { 
      console.error('Complete error:', err.response?.data || err.message);
      alert('Error completing induction: ' + (err.response?.data?.error || err.message)); 
    }
  };

  const startNewInduction = async () => {
    setStartingInduction(true);
    try {
      const res = await axios.post(`${API}/inductions`, {
        user_id: selected.id,
        inducted_by: user.id,
        induction_date: new Date().toISOString().split('T')[0],
        created_by: user.id,
      });
      const full = await axios.get(`${API}/inductions/${res.data.id}`);
      setInductionSections(full.data.sections || []);
      setInductionDetail({ ...res.data, inducted_by: { first_name: user.first_name, last_name: user.last_name } });
      // Refresh profile inductions
      const indRes = await axios.get(`${API}/inductions/user/${selected.id}`);
      setProfile(prev => ({ ...prev, inductions: indRes.data }));
    } catch (err) { alert('Error starting induction.'); }
    finally { setStartingInduction(false); }
  };

  const generateCertificate = async (ind) => {
    try {
      const res = await axios.post(`${API}/inductions/${ind.id}/certificate`);
      // Update local induction with certificate URL
      setProfile(prev => ({
        ...prev,
        inductions: prev.inductions.map(i =>
          i.id === ind.id ? { ...i, certificate_url: res.data.url } : i
        )
      }));
      window.open(res.data.url, '_blank');
    } catch (err) { alert('Error generating certificate.'); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/users/${selected.id}`, editForm);
      const updated = { ...selected, ...editForm };
      setSelected(updated);
      setStaff(prev => prev.map(s => s.id === updated.id ? updated : s));
      setEditing(false);
    } catch (err) { alert('Error saving profile.'); }
    finally { setSaving(false); }
  };

  const handleArchive = async () => {
    if (!window.confirm(`Archive ${selected.first_name} ${selected.last_name}? They will no longer appear in the active staff list.`)) return;
    try {
      await axios.patch(`${API}/users/${selected.id}/deactivate`, { archived_by: user.id });
      setSelected(null);
      setProfile(null);
      fetchStaff();
    } catch (err) { alert('Error archiving staff member.'); }
  };

  const handleResetPassword = async () => {
    if (!window.confirm(`Reset ${selected.first_name} ${selected.last_name}'s password back to the default? They should change it themselves after logging in.`)) return;
    try {
      const res = await axios.post(`${API}/users/${selected.id}/reset-password-default`, { reset_by: user.id });
      alert(res.data.message || 'Password reset.');
    } catch (err) { alert(err.response?.data?.error || 'Error resetting password.'); }
  };

  const handleUnarchive = async () => {
    try {
      await axios.patch(`${API}/users/${selected.id}`, {
        is_active: true,
        is_archived: false,
        archived_at: null,
        archived_by: null,
      });
      const updated = { ...selected, is_active: true, is_archived: false };
      setSelected(updated);
      fetchStaff();
    } catch (err) { alert('Error restoring staff member.'); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please select an image file.'); return; }

    const ext = file.name.split('.').pop();
    const filename = `${selected.first_name.toLowerCase()}-${selected.last_name.toLowerCase()}-photo.${ext}`;
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Upload to Supabase storage via fetch
      
      const uploadRes = await fetch(`${SUPABASE_URL}/storage/v1/object/vera-documents/staff-photos/${filename}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${SUPABASE_KEY}`, 'x-upsert': 'true' },
        body: file,
      });
      if (!uploadRes.ok) throw new Error('Upload failed');
      const photoUrl = `${SUPABASE_URL}/storage/v1/object/public/vera-documents/staff-photos/${filename}`;
      await axios.patch(`${API}/users/${selected.id}`, { photo_url: photoUrl });
      setSelected(prev => ({ ...prev, photo_url: photoUrl }));
      setStaff(prev => prev.map(s => s.id === selected.id ? { ...s, photo_url: photoUrl } : s));
    } catch (err) { alert('Error uploading photo. You can also upload manually via Supabase storage.'); }
  };

  const handleAddNew = async () => {
    if (!newForm.first_name || !newForm.last_name || !newForm.email) {
      alert('First name, last name and email are required.');
      return;
    }
    setAddingSaving(true);
    try {
      const res = await axios.post(`${API}/users`, { ...newForm, created_by: user.id });
      setAddingNew(false);
      setNewForm({ first_name: '', last_name: '', email: '', job_title: '', role: 'staff', phone: '', date_of_birth: '', start_date: '', ni_number: '' });
      fetchStaff();
      alert(`${newForm.first_name} ${newForm.last_name} added. Their temporary password is: ${res.data.default_password}`);
    } catch (err) { alert(err.response?.data?.error || 'Error adding new staff member.'); }
    finally { setAddingSaving(false); }
  };

  const getInitials = (u) => `${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}`;

  const formatPhone = (value) => {
    let cleaned = value.replace(/[^\d+]/g, '');
    if (cleaned.startsWith('+44')) cleaned = '0' + cleaned.slice(3);
    if (cleaned.length === 11 && cleaned.startsWith('0')) {
      if (cleaned.startsWith('07')) return cleaned.replace(/(\d{5})(\d{3})(\d{3})/, '$1 $2 $3');
      return cleaned.replace(/(\d{4})(\d{3})(\d{4})/, '$1 $2 $3');
    }
    return cleaned;
  };

  const daysUntilExpiry = (date) => Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24));

  const expiryStatus = (date) => {
    if (!date) return null;
    const days = daysUntilExpiry(date);
    if (days < 0) return 'overdue';
    if (days <= 90) return 'expiring';
    return 'current';
  };

  const activeStaff = staff.filter(s => s.is_active && !s.is_archived);
  const archivedStaff = staff.filter(s => !s.is_active || s.is_archived);

  if (loading) return <div className="staff-loading">Loading staff...</div>;

  // INDUCTION DETAIL VIEW
  if (inductionDetail) return (
    <div className="staff-profile">
      <button className="staff-back" onClick={() => setInductionDetail(null)}>← BACK TO PROFILE</button>
      <div className="induction-detail-header">
        <div>
          <h2>{selected.first_name} {selected.last_name}</h2>
          <p className="induction-detail-meta">Conducted by {inductionDetail.inducted_by?.first_name} {inductionDetail.inducted_by?.last_name} on {new Date(inductionDetail.induction_date).toLocaleDateString('en-GB')}</p>
        </div>
        <div className={`induction-status ${inductionDetail.status === 'completed' ? 'completed' : 'in-progress'}`}>
          {inductionDetail.status === 'completed' ? '✓ COMPLETED' : 'IN PROGRESS'}
        </div>
      </div>
      <div className="induction-sections-list">
        {inductionSections.slice().sort((a, b) => (a.template_item?.section_number || 0) - (b.template_item?.section_number || 0)).map(s => (
          <div key={s.id} className={`induction-section-row ${s.completed ? 'completed' : ''}`} onClick={() => toggleSection(s)} style={{ cursor: inductionDetail.status === 'completed' ? 'default' : 'pointer' }}>
            <div className={`section-check ${s.completed ? 'done' : ''}`}>{s.completed ? '✓' : s.template_item?.section_number}</div>
            <div className="section-detail">
              <div className="section-detail-title">
                {s.template_item?.title}
                {s.template_item?.requires_signoff && <span className="signoff-tag">SIGN OFF</span>}
              </div>
              <div className="section-detail-desc">{s.template_item?.description}</div>
            </div>
            {s.completed && s.completed_at && <div className="section-time">{new Date(s.completed_at).toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'})}</div>}
          </div>
        ))}
      </div>

      {/* Signoff buttons — only show when all sections complete and not yet completed */}
      {inductionDetail.status !== 'completed' && inductionSections.length > 0 && inductionSections.every(s => s.completed) && (
        <div className="induction-signoff-actions">
          <p className="signoff-prompt">All sections covered — record sign off below to complete the induction.</p>
          <div className="signoff-buttons">
            <button
              className={`signoff-btn employee ${inductionDetail.employee_signed ? 'signed' : ''}`}
              onClick={!inductionDetail.employee_signed ? employeeSignOff : undefined}
              disabled={inductionDetail.employee_signed}
            >
              {inductionDetail.employee_signed ? `✓ ${selected.first_name} confirmed` : `${selected.first_name} ${selected.last_name} — CONFIRM`}
            </button>
            {inductionDetail.employee_signed && user.role === 'admin' && (
              <button
                className={`signoff-btn company ${inductionDetail.company_rep_signed ? 'signed' : ''}`}
                onClick={!inductionDetail.company_rep_signed ? completeInduction : undefined}
                disabled={inductionDetail.company_rep_signed}
              >
                {inductionDetail.company_rep_signed ? `✓ ${user.first_name} confirmed` : `${user.first_name} ${user.last_name} — COMPLETE INDUCTION`}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="induction-signoff-summary">
        <div className={`signoff-row ${inductionDetail.employee_signed ? 'signed' : ''}`}>
          <span>{inductionDetail.employee_signed ? '✓' : '○'}</span>
          <span>Employee sign off — <strong>{selected.first_name} {selected.last_name}</strong></span>
          {inductionDetail.employee_signed_at && <span>{new Date(inductionDetail.employee_signed_at).toLocaleDateString('en-GB')}</span>}
        </div>
        <div className={`signoff-row ${inductionDetail.company_rep_signed ? 'signed' : ''}`}>
          <span>{inductionDetail.company_rep_signed ? '✓' : '○'}</span>
          <span>Company representative sign off — <strong>{inductionDetail.inducted_by?.first_name} {inductionDetail.inducted_by?.last_name}</strong></span>
          {inductionDetail.company_rep_signed_at && <span>{new Date(inductionDetail.company_rep_signed_at).toLocaleDateString('en-GB')}</span>}
        </div>
      </div>
    </div>
  );

  // EDIT FORM VIEW
  if (selected && editing && user.role === 'admin') return (
    <div className="staff-profile">
      <button className="staff-back" onClick={() => setEditing(false)}>← BACK TO PROFILE</button>
      <div className="edit-header">
        <div className="profile-avatar large">{getInitials(selected)}</div>
        <h2>EDIT — {selected.first_name} {selected.last_name}</h2>
      </div>
      <div className="edit-form">
        <div className="edit-section-title">PERSONAL DETAILS</div>
        <div className="edit-grid">
          <div className="edit-field"><label>FIRST NAME</label><input value={editForm.first_name || ''} onChange={e => setEditForm({...editForm, first_name: e.target.value})} /></div>
          <div className="edit-field"><label>LAST NAME</label><input value={editForm.last_name || ''} onChange={e => setEditForm({...editForm, last_name: e.target.value})} /></div>
          <div className="edit-field"><label>EMAIL</label><input value={editForm.email || ''} onChange={e => setEditForm({...editForm, email: e.target.value})} /></div>
          <div className="edit-field"><label>PHONE</label><input value={editForm.phone || ''} onChange={e => setEditForm({...editForm, phone: formatPhone(e.target.value)})} placeholder="e.g. 07700 900000" /></div>
          <div className="edit-field"><label>DATE OF BIRTH</label><input type="date" value={editForm.date_of_birth || ''} onChange={e => setEditForm({...editForm, date_of_birth: e.target.value})} /></div>
          <div className="edit-field"><label>NI NUMBER</label><input value={editForm.ni_number || ''} onChange={e => setEditForm({...editForm, ni_number: e.target.value})} placeholder="e.g. AB 12 34 56 C" /></div>
          <div className="edit-field"><label>START DATE</label><input type="date" value={editForm.start_date || ''} onChange={e => setEditForm({...editForm, start_date: e.target.value})} /></div>
        </div>
        <div className="edit-section-title">SYSTEM ACCESS</div>
        <div className="edit-grid">
          <div className="edit-field"><label>JOB TITLE</label><input value={editForm.job_title || ''} onChange={e => setEditForm({...editForm, job_title: e.target.value})} /></div>
          <div className="edit-field"><label>ROLE</label>
            <select value={editForm.role || 'staff'} onChange={e => setEditForm({...editForm, role: e.target.value})}>
              <option value="staff">Staff</option>
              <option value="admin">Admin</option>
              <option value="auditor">Auditor</option>
            </select>
          </div>
        </div>
        <div className="edit-section-title">EMERGENCY CONTACT</div>
        <div className="edit-grid">
          <div className="edit-field"><label>CONTACT NAME</label><input value={editForm.emergency_contact_name || ''} onChange={e => setEditForm({...editForm, emergency_contact_name: e.target.value})} placeholder="Full name" /></div>
          <div className="edit-field"><label>CONTACT PHONE</label><input value={editForm.emergency_contact_phone || ''} onChange={e => setEditForm({...editForm, emergency_contact_phone: formatPhone(e.target.value)})} placeholder="e.g. 07700 900000" /></div>
        </div>
        <div className="edit-actions">
          <button className="edit-btn secondary" onClick={() => setEditing(false)}>CANCEL</button>
          <button className={`edit-btn primary ${saving ? 'disabled' : ''}`} onClick={handleSave} disabled={saving}>{saving ? 'SAVING...' : 'SAVE CHANGES'}</button>
        </div>
      </div>
    </div>
  );

  // ADD NEW EMPLOYEE VIEW
  if (addingNew && user.role === 'admin') return (
    <div className="staff-profile">
      <button className="staff-back" onClick={() => setAddingNew(false)}>← STAFF DIRECTORY</button>
      <div className="edit-header">
        <div className="profile-avatar large new-avatar">+</div>
        <h2>ADD NEW STAFF MEMBER</h2>
      </div>
      <div className="edit-form">
        <div className="edit-section-title">PERSONAL DETAILS</div>
        <div className="edit-grid">
          <div className="edit-field"><label>FIRST NAME *</label><input value={newForm.first_name} onChange={e => setNewForm({...newForm, first_name: e.target.value})} placeholder="First name" /></div>
          <div className="edit-field"><label>LAST NAME *</label><input value={newForm.last_name} onChange={e => setNewForm({...newForm, last_name: e.target.value})} placeholder="Last name" /></div>
          <div className="edit-field"><label>EMAIL *</label><input value={newForm.email} onChange={e => setNewForm({...newForm, email: e.target.value})} placeholder="name@bramleyengineering.co.uk" /></div>
          <div className="edit-field"><label>PHONE</label><input value={newForm.phone} onChange={e => setNewForm({...newForm, phone: formatPhone(e.target.value)})} placeholder="e.g. 07700 900000" /></div>
          <div className="edit-field"><label>DATE OF BIRTH</label><input type="date" value={newForm.date_of_birth} onChange={e => setNewForm({...newForm, date_of_birth: e.target.value})} /></div>
          <div className="edit-field"><label>NI NUMBER</label><input value={newForm.ni_number} onChange={e => setNewForm({...newForm, ni_number: e.target.value})} placeholder="e.g. AB 12 34 56 C" /></div>
          <div className="edit-field"><label>START DATE</label><input type="date" value={newForm.start_date} onChange={e => setNewForm({...newForm, start_date: e.target.value})} /></div>
        </div>
        <div className="edit-section-title">SYSTEM ACCESS</div>
        <div className="edit-grid">
          <div className="edit-field"><label>JOB TITLE</label><input value={newForm.job_title} onChange={e => setNewForm({...newForm, job_title: e.target.value})} placeholder="e.g. Site Fitter" /></div>
          <div className="edit-field"><label>ROLE</label>
            <select value={newForm.role} onChange={e => setNewForm({...newForm, role: e.target.value})}>
              <option value="staff">Staff</option>
              <option value="admin">Admin</option>
              <option value="auditor">Auditor</option>
            </select>
          </div>
        </div>
        <div className="new-password-note">
          <span>🔑</span> Default password is their last name in lowercase (short surnames are padded automatically) — shown here once they're added. They can change it any time, or use "Forgotten your password?" on the sign-in screen.
        </div>
        <div className="edit-actions">
          <button className="edit-btn secondary" onClick={() => setAddingNew(false)}>CANCEL</button>
          <button className={`edit-btn primary ${addingSaving ? 'disabled' : ''}`} onClick={handleAddNew} disabled={addingSaving}>{addingSaving ? 'ADDING...' : 'ADD STAFF MEMBER'}</button>
        </div>
      </div>
    </div>
  );

  // PROFILE VIEW
  if (selected) return (
    <div className="staff-profile">
      <button className="staff-back" onClick={() => { setSelected(null); setProfile(null); }}>← STAFF DIRECTORY</button>
      <div className="profile-header">
        <div className="profile-picture-placeholder">
          {selected.photo_url ? (
            <img src={selected.photo_url} alt={selected.first_name} className="profile-photo" />
          ) : (
            <div className="profile-avatar large">{getInitials(selected)}</div>
          )}
          {user.role === 'admin' && (
            <label className="photo-upload-label">
              {selected.photo_url ? 'CHANGE PHOTO' : 'ADD PHOTO'}
              <input type="file" accept="image/*" onChange={handlePhotoUpload} style={{display:'none'}} />
            </label>
          )}
        </div>
        <div className="profile-info">
          <h2>{selected.first_name} {selected.last_name}</h2>
          <span className="profile-title">{selected.job_title}</span>
          <span className={`profile-role-badge ${selected.role}`}>{selected.role.toUpperCase()}</span>
        </div>
        <div className="profile-meta">
          <div className="meta-item"><span className="meta-label">EMAIL</span><span className="meta-value">{selected.email}</span></div>
          {selected.phone && <div className="meta-item"><span className="meta-label">PHONE</span><span className="meta-value">{selected.phone}</span></div>}
          {selected.date_of_birth && <div className="meta-item"><span className="meta-label">DATE OF BIRTH</span><span className="meta-value">{new Date(selected.date_of_birth).toLocaleDateString('en-GB')}</span></div>}
          {selected.ni_number && <div className="meta-item"><span className="meta-label">NI NUMBER</span><span className="meta-value">{selected.ni_number}</span></div>}
          {selected.start_date && <div className="meta-item"><span className="meta-label">START DATE</span><span className="meta-value">{new Date(selected.start_date).toLocaleDateString('en-GB')}</span></div>}
          {selected.emergency_contact_name && <div className="meta-item"><span className="meta-label">EMERGENCY CONTACT</span><span className="meta-value">{selected.emergency_contact_name} — {selected.emergency_contact_phone}</span></div>}
          <div className="meta-item"><span className="meta-label">STATUS</span><span className={`meta-value ${selected.is_active ? 'active' : 'inactive'}`}>{selected.is_active ? 'ACTIVE' : 'INACTIVE'}</span></div>
          {user.role === 'admin' && (
            <div className="profile-actions">
              <button className="edit-profile-btn" onClick={() => setEditing(true)}>EDIT PROFILE</button>
              <button className="edit-profile-btn" onClick={handleResetPassword}>RESET PASSWORD</button>
              {selected.is_active && <button className="archive-btn" onClick={handleArchive}>ARCHIVE EMPLOYEE</button>}
              {!selected.is_active && <button className="unarchive-btn" onClick={handleUnarchive}>RESTORE EMPLOYEE</button>}
            </div>
          )}
        </div>
      </div>

      {profileLoading ? <div className="profile-loading">Loading profile...</div> : profile && (
        <>
          <div className="profile-section">
            <div className="profile-section-header">
              <h3 className="profile-section-title">TRAINING & CERTIFICATES</h3>
              {user.role === 'admin' && !showTrainingForm && (
                <button className="add-training-btn" onClick={() => setShowTrainingForm(true)}>+ ADD TRAINING</button>
              )}
            </div>

            {showTrainingForm && (
              <div className="training-upload-form">
                {/* Smart drop zone */}
                <div
                  className={`training-drop-zone ${trainingForm.certificate_url ? 'has-file' : ''}`}
                  onClick={() => trainingFileRef.current?.click()}
                >
                  {uploadingTraining || analysingTraining ? (
                    <div className="training-drop-analysing">
                      <div className="training-drop-spinner" />
                      <span>{uploadingTraining ? 'Uploading...' : 'Reading certificate...'}</span>
                    </div>
                  ) : trainingForm.certificate_url ? (
                    <div className="training-drop-done">
                      <span className="training-drop-tick">✓</span>
                      <span>Certificate uploaded — fields pre-filled below</span>
                      <button className="training-drop-change" onClick={e => { e.stopPropagation(); trainingFileRef.current?.click(); }}>CHANGE</button>
                    </div>
                  ) : (
                    <div className="training-drop-empty">
                      <div className="training-drop-icon">⬆</div>
                      <div className="training-drop-text">Drop certificate here or click to browse</div>
                      <div className="training-drop-hint">Claude will read the cert and fill in the details</div>
                    </div>
                  )}
                  <input ref={trainingFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={e => uploadAndAnalyseTraining(e.target.files[0])} />
                </div>

                {/* Form fields */}
                <div className="training-form-grid">
                  <div className="training-form-field full">
                    <label>TRAINING TYPE *</label>
                    <input value={trainingForm.training_type} onChange={e => setTrainingForm({...trainingForm, training_type: e.target.value})} placeholder="e.g. CSCS, IPAF, First Aid, PASMA" />
                  </div>
                  <div className="training-form-field">
                    <label>PROVIDER / ISSUING BODY</label>
                    <input value={trainingForm.provider} onChange={e => setTrainingForm({...trainingForm, provider: e.target.value})} placeholder="e.g. CITB, IPAF, St John Ambulance" />
                  </div>
                  <div className="training-form-field">
                    <label>CERTIFICATE NUMBER</label>
                    <input value={trainingForm.certificate_number} onChange={e => setTrainingForm({...trainingForm, certificate_number: e.target.value})} />
                  </div>
                  <div className="training-form-field">
                    <label>DATE COMPLETED / ISSUED</label>
                    <input type="date" value={trainingForm.issue_date} onChange={e => setTrainingForm({...trainingForm, issue_date: e.target.value})} />
                  </div>
                  <div className="training-form-field">
                    <label>EXPIRY DATE</label>
                    <input type="date" value={trainingForm.expiry_date} onChange={e => setTrainingForm({...trainingForm, expiry_date: e.target.value})} />
                  </div>
                  <div className="training-form-field full">
                    <label>NOTES</label>
                    <input value={trainingForm.notes} onChange={e => setTrainingForm({...trainingForm, notes: e.target.value})} placeholder="Any additional notes..." />
                  </div>
                </div>
                <div className="training-form-actions">
                  <button className="training-cancel-btn" onClick={() => { setShowTrainingForm(false); setTrainingForm({ training_type: '', provider: '', issue_date: '', expiry_date: '', certificate_number: '', certificate_url: '', notes: '' }); }}>CANCEL</button>
                  <button className="training-save-btn" onClick={saveTrainingRecord} disabled={savingTraining}>{savingTraining ? 'SAVING...' : 'SAVE TRAINING RECORD'}</button>
                </div>
              </div>
            )}

            {profile.training.length === 0 && !showTrainingForm ? <div className="profile-empty">No training records on file.</div> : (
              <div className="training-list">
                {profile.training.map(t => {
                  const status = expiryStatus(t.expiry_date);
                  const days = t.expiry_date ? daysUntilExpiry(t.expiry_date) : null;
                  return (
                    <div key={t.id} className={`training-card ${status || ''}`} onClick={() => setSelectedTraining(t)} style={{ cursor: 'pointer' }}>
                      <div className="training-card-left">
                        <div className="training-type">{t.training_type}</div>
                        <div className="training-provider">{t.provider}</div>
                        {t.notes && <div className="training-notes">{t.notes}</div>}
                      </div>
                      <div className="training-card-right">
                        {t.expiry_date && <div className={`training-expiry ${status}`}>{status === 'overdue' ? 'OVERDUE' : status === 'expiring' ? `${days}d remaining` : `Expires ${new Date(t.expiry_date).toLocaleDateString('en-GB')}`}</div>}
                        {t.certificate_url && <a href={t.certificate_url} target="_blank" rel="noreferrer" className="training-cert-link" onClick={e => e.stopPropagation()}>VIEW CERT</a>}
                        {user.role === 'admin' && <button className="training-archive-btn" onClick={e => { e.stopPropagation(); handleArchiveTraining(t.id); }}>ARCHIVE</button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Training detail modal */}
          {selectedTraining && (
            <div className="tr-modal-overlay" onClick={() => setSelectedTraining(null)}>
              <div className="tr-modal" onClick={e => e.stopPropagation()}>
                <button className="tr-modal-close" onClick={() => setSelectedTraining(null)}>✕</button>
                <div className="tr-modal-header">
                  <h3>{selectedTraining.training_type}</h3>
                  <p>{selectedTraining.provider}</p>
                </div>
                <div className="tr-modal-body">
                  {selectedTraining.date_completed && <div className="tr-modal-row"><span>Date Completed</span><span>{new Date(selectedTraining.date_completed).toLocaleDateString('en-GB')}</span></div>}
                  {selectedTraining.expiry_date && <div className="tr-modal-row"><span>Expiry Date</span><span className={expiryStatus(selectedTraining.expiry_date) === 'overdue' ? 'tr-expired' : expiryStatus(selectedTraining.expiry_date) === 'expiring' ? 'tr-expiring' : 'tr-valid'}>{new Date(selectedTraining.expiry_date).toLocaleDateString('en-GB')}</span></div>}
                  {selectedTraining.certificate_number && <div className="tr-modal-row"><span>Certificate No.</span><span>{selectedTraining.certificate_number}</span></div>}
                  {selectedTraining.notes && <div className="tr-modal-notes"><div className="tr-modal-notes-label">NOTES</div><p>{selectedTraining.notes}</p></div>}
                </div>
                <div className="tr-modal-footer">
                  {selectedTraining.certificate_url ? (
                    <a href={selectedTraining.certificate_url} target="_blank" rel="noreferrer" className="tr-view-btn">VIEW CERTIFICATE ↗</a>
                  ) : (
                    <span className="tr-no-cert">No certificate uploaded</span>
                  )}
                  {user.role === 'admin' && (
                    <button className="tr-archive-btn" onClick={() => { handleArchiveTraining(selectedTraining.id); setSelectedTraining(null); }}>ARCHIVE</button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="profile-section">
            <h3 className="profile-section-title">INDUCTIONS</h3>
            {profile.inductions.length === 0 ? (
              <div className="profile-empty">
                No induction on record.
                {user.role === 'admin' && (
                  <button className="start-induction-btn" onClick={startNewInduction} disabled={startingInduction}>
                    {startingInduction ? 'STARTING...' : '+ START INDUCTION'}
                  </button>
                )}
              </div>
            ) : (
              <div className="induction-list">
                {profile.inductions.map(ind => (
                  <div key={ind.id} className={`induction-card ${ind.status === 'completed' ? 'completed' : 'in-progress'}`}>
                    <div className="induction-card-left">
                      <div className="induction-date">{new Date(ind.induction_date).toLocaleDateString('en-GB')}</div>
                      <div className="induction-by">Inducted by {ind.inducted_by?.first_name} {ind.inducted_by?.last_name}</div>
                    </div>
                    <div className="induction-card-right">
                      <div className={`induction-status ${ind.status === 'completed' ? 'completed' : 'in-progress'}`}>{ind.status === 'completed' ? '✓ COMPLETED' : 'IN PROGRESS'}</div>
                      {ind.completed_at && <div className="induction-completed-at">{new Date(ind.completed_at).toLocaleDateString('en-GB')}</div>}
                      <button className="view-induction-btn" onClick={() => fetchInductionDetail(ind)}>VIEW DETAILS</button>
                      {ind.certificate_url ? (
                        <a href={ind.certificate_url} target="_blank" rel="noreferrer" className="view-induction-btn cert-link" onClick={e => e.stopPropagation()}>VIEW CERTIFICATE</a>
                      ) : ind.status === 'completed' && user.role === 'admin' && (
                        <button className="view-induction-btn cert-link" onClick={e => { e.stopPropagation(); generateCertificate(ind); }}>GENERATE CERTIFICATE</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  // DIRECTORY VIEW
  return (
    <div className="staff-directory">
      <div className="staff-directory-header">
        <div className="staff-count">{activeStaff.length} active staff members</div>
        {user.role === 'admin' && (
          <div className="staff-header-actions">
            <button className="staff-toggle-archived" onClick={() => setShowArchived(!showArchived)}>
              {showArchived ? 'HIDE ARCHIVED' : `SHOW ARCHIVED (${archivedStaff.length})`}
            </button>
            <button className="staff-toggle-archived" onClick={() => setShowLoginReport(true)}>LOGIN ACTIVITY</button>
            <button className="staff-add-btn" onClick={() => setAddingNew(true)}>+ ADD STAFF</button>
          </div>
        )}
      </div>

      {showLoginReport && <LoginReportModal onClose={() => setShowLoginReport(false)} />}

      <div className="staff-grid">
        {activeStaff.map(s => {
          const training = trainingMap[s.id] || [];
          const inductions = inductionMap[s.id] || [];
          const inducted = inductions.some(i => i.status === 'completed');
          return (
            <div key={s.id} className="staff-card" onClick={() => fetchProfile(s)}>
              <div className="staff-card-avatar">{getInitials(s)}</div>
              <div className="staff-card-info">
                <div className="staff-card-name">{s.first_name} {s.last_name}</div>
                <div className="staff-card-title">{s.job_title}{s.date_of_birth ? <span className="staff-card-dob"> · DOB: {new Date(s.date_of_birth).toLocaleDateString('en-GB')}</span> : ''}</div>
                <div className="staff-card-badges-row">
                  <span className={`staff-card-badge ${s.role}`}>{s.role}</span>
                  <span className={`training-badge ${inducted ? 'current' : 'overdue'}`} title="Induction">
                    INDUCTION {inducted ? '✓' : '✗'}
                  </span>
                  {training.map(t => {
                    const days = t.expiry_date ? daysUntilExpiry(t.expiry_date) : null;
                    const status = expiryStatus(t.expiry_date);
                    const shortName = t.training_type.split(/[\s—-]/)[0].substring(0, 10).toUpperCase();
                    return (
                      <span key={t.id} className={`training-badge ${status || 'no-expiry'}`} title={t.training_type}>
                        {shortName}{days !== null && <span className="badge-days"> {days < 0 ? 'EXP' : `${days}d`}</span>}
                      </span>
                    );
                  })}
                </div>
              </div>
              <div className="staff-card-arrow">→</div>
            </div>
          );
        })}
      </div>

      {showArchived && archivedStaff.length > 0 && (
        <div className="archived-section">
          <h3 className="archived-title">ARCHIVED EMPLOYEES</h3>
          <div className="staff-grid">
            {archivedStaff.map(s => (
              <div key={s.id} className="staff-card archived" onClick={() => fetchProfile(s)}>
                <div className="staff-card-avatar archived-avatar">{getInitials(s)}</div>
                <div className="staff-card-info">
                  <div className="staff-card-name">{s.first_name} {s.last_name}</div>
                  <div className="staff-card-title">{s.job_title}</div>
                  <span className="staff-card-badge archived-badge">ARCHIVED</span>
                </div>
                <div className="staff-card-arrow">→</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
